import React, { useEffect, useState } from "react";
import { Page } from "@/components/page-base";
import {
  MainContainer,
  RangeContainer,
  RangeLabel,
  SectionContainer,
  TopCardsContainer
} from "./styles";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { Input } from "@/components/inputs";
import { DataCard } from "../components/data-card";

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  LineElement,
  PointElement
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import isMobile from "is-mobile";

import { DynamicPieChart } from "../components/pie";
import DynamicBarChart from "../components/bars";
import DynamicLineChart from "../components/line";
import { BIService } from "../services";
import {
  IPromptsCountResponse,
  TConsultedResponse,
  TDailyProcessesRes,
  TDailySignUps,
  TDailySingUpsRes,
  TPlanCountResponse,
  TReceivedMoney,
  TRegionCountResponse,
  TUsersProcess,
  TVariation
} from "../models/interfaces/dashboard";
import { DateTime, Duration } from "luxon";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup
} from "@mui/material";

ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend,
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  LineElement,
  PointElement
);
type Interval = "oneDay" | "oneMonth" | "threeMonths" | "oneYear";

const OFFSETS: Record<Interval, Duration> = {
  oneDay: Duration.fromObject({ days: 1 }),
  oneMonth: Duration.fromObject({ months: 1 }),
  threeMonths: Duration.fromObject({ months: 3 }),
  oneYear: Duration.fromObject({ years: 1 })
};

export const MasterDashboard: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [displayError, setDisplayError] = useState<boolean>(false);

  const [processesLinesAmount, setProcessesLinesAmount] =
    useState<TVariation>();
  const [processesAmount, setProcessesAmount] = useState<TVariation>();
  const [analysisAmount, setAnalysisAmount] = useState<TVariation>();
  const [peopleAmount, setPeopleAmount] = useState<TVariation>();
  const [companyAmount, setCompanyAmount] = useState<TVariation>();
  const [nonVerifiedUsersAmount, setNonVerifiedUsersAmount] =
    useState<TVariation>();
  const [dailySignups, setDailySignups] = useState<TDailySingUpsRes>();
  const [dailyProcesses, setDailyProcesses] = useState<TDailyProcessesRes>();
  const [receivedMoney, setReceivedMoney] = useState<TReceivedMoney>();
  const [signupAmount, setSignupAmount] = useState<TVariation>();
  const [usersProcess, setUsersProcess] = useState<TUsersProcess>();
  const [regionCount, setRegionCount] = useState<TRegionCountResponse>();
  const [planCount, setPlanCount] = useState<TPlanCountResponse>();
  const [consulted, setConsulted] = useState<TConsultedResponse>();
  const [scenarios, setScenarios] = useState<IPromptsCountResponse>();
  const [interval, setInterval] = useState<Interval | "manual">("manual");

  useEffect(() => {
    if (startDate && endDate) {
      if (startDate.getTime() > endDate.getTime()) {
        setDisplayError(true);
      } else {
        setDisplayError(false);
        populateData(
          startDate?.toISOString() ?? "",
          endDate?.toISOString() ?? ""
        );
      }
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const today = DateTime.local();
    const offset = interval !== "manual" ? OFFSETS[interval] : undefined;

    if (offset) {
      const start = today.minus(offset);
      setStartDate(start.toJSDate());
      setEndDate(today.toJSDate());
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [interval]);

  const populateData = async (startDate: string, endDate: string) => {
    const res = await BIService.buildDashboard(startDate, endDate);
    if (res.success) {
      setProcessesLinesAmount(res.processesLinesAmount);
      setProcessesAmount(res.processesAmount);
      setAnalysisAmount(res.analysisAmount);
      setPeopleAmount(res.peopleAmount);
      setCompanyAmount(res.companyAmount);
      setNonVerifiedUsersAmount(res.nonVerifiedUsersAmount);
      setDailySignups(res.dailySignups);
      setReceivedMoney(res.receivedMoney);
      setSignupAmount(res.signupAmount);
      setUsersProcess(res.analyzersAmount);
      setRegionCount(res.regionCount);
      setPlanCount(res.planCount);
      setConsulted(res.consultedStatusAmount);
      setScenarios(res.scenariosAmount);
      setDailyProcesses(res.dailyProcesses);
    } else {
      console.error("Erro ao carregar os dados do dashboard");
    }
  };

  const formatDateTime = (value: string): string => {
    const dt = DateTime.fromISO(value);
    return dt.isValid ? dt.setLocale("pt-BR").toFormat("dd/LL/yyyy") : value;
  };

  const parseDailyLabels = (dailySignups: TDailySignUps[]) => {
    if (!dailySignups) return [];
    return dailySignups.map((item) => formatDateTime(item.date));
  };
  const parseDailyData = (dailySignups: TDailySignUps[]) => {
    if (!dailySignups) return [];
    return dailySignups.map((item) => item.total);
  };

  const handleInterval = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInterval(event.target.value as Interval | "manual");
  };

  return (
    <Page.Root>
      <Page.Header
        title="Relatórios"
        controlElement={[]}
        iconPage={<TrendingUpOutlinedIcon />}
      />
      <MainContainer>
        <Input.DateProvider>
          <RangeContainer>
            <div className="row">
              <RangeLabel>
                Defina o intervalo para análise das métricas:
              </RangeLabel>
              <Input.DatePicker
                label="Data Início"
                value={startDate}
                setValue={setStartDate}
              />
              <Input.DatePicker
                label="Data Fim"
                value={endDate}
                setValue={setEndDate}
              />
            </div>
            <FormControl>
              <RadioGroup row value={interval} onChange={handleInterval}>
                <FormControlLabel
                  value="manual"
                  control={<Radio />}
                  label="Manual"
                />
                <FormControlLabel
                  value="oneDay"
                  control={<Radio />}
                  label="Um Dia"
                />
                <FormControlLabel
                  value="oneMonth"
                  control={<Radio />}
                  label="Um Mês"
                />
                <FormControlLabel
                  value="threeMonths"
                  control={<Radio />}
                  label="Três Meses"
                />
                <FormControlLabel
                  value="oneYear"
                  control={<Radio />}
                  label="Um Ano"
                />
              </RadioGroup>
            </FormControl>
          </RangeContainer>
          <Page.Error
            display={displayError}
            message="Erro: Verificar intervalo entre as datas."
          />
        </Input.DateProvider>
        <TopCardsContainer>
          <DataCard
            icon="ContentPasteSearchOutlined"
            label="Qtd Consultas"
            value={analysisAmount?.currentCount ?? 0}
            percentage={analysisAmount?.variation ?? 0}
            hidePercentage={analysisAmount?.variation ? false : true}
          />
          <DataCard
            icon="AttachMoneyOutlined"
            label="Valor Recebido"
            value={receivedMoney?.currentReceivedMoney ?? 0}
            percentage={receivedMoney?.variation ?? 0}
            hidePercentage={receivedMoney?.variation ? false : true}
            formating="currency"
          />

          <DataCard
            icon="PersonOutlineOutlined"
            label={"Qtd PF"}
            value={peopleAmount?.currentCount ?? 0}
            percentage={peopleAmount?.variation ?? 0}
            hidePercentage={peopleAmount?.variation ? false : true}
          />
          <DataCard
            icon="AssignmentIndOutlined"
            label={"Qtd PJ"}
            value={companyAmount?.currentCount ?? 0}
            percentage={companyAmount?.variation ?? 0}
            hidePercentage={companyAmount?.variation ? false : true}
          />
        </TopCardsContainer>
        <TopCardsContainer>
          <DataCard
            icon="PersonOffOutlined"
            label={"Contas não verificadas"}
            value={nonVerifiedUsersAmount?.currentCount ?? 0}
            percentage={nonVerifiedUsersAmount?.variation ?? 0}
            hidePercentage={nonVerifiedUsersAmount?.variation ? false : true}
          />
          <DataCard
            icon="FindInPageOutlined"
            label={"Qtd de linhas"}
            value={processesLinesAmount?.currentCount ?? 0}
            percentage={processesLinesAmount?.variation ?? 0}
            hidePercentage={processesLinesAmount?.variation ? false : true}
          />
          <DataCard
            icon="ContentCopyOutlined"
            label={"Qtd de processos"}
            value={processesAmount?.currentCount ?? 0}
            percentage={processesAmount?.variation ?? 0}
            hidePercentage={processesAmount?.variation ? false : true}
          />
          <DataCard
            icon="PersonAddAlt1Outlined"
            label="Qtd Cadastro"
            value={signupAmount?.currentCount ?? 0}
            percentage={signupAmount?.variation ?? 0}
            hidePercentage={signupAmount?.variation ? false : true}
          />
        </TopCardsContainer>
        <SectionContainer>
          <div style={{ width: "300px", height: "300px", flex: 1 }}>
            <DynamicPieChart
              data={[
                usersProcess?.usersWithProcess ?? 0,
                usersProcess?.usersWithoutProcess ?? 0
              ]}
              labels={[
                "Usuários que já consultaram",
                "Usuários que não consultaram"
              ]}
              dataTitle="Consultas"
              seed={6}
            />
          </div>
          <div style={{ width: "300px", height: "300px", flex: 1 }}>
            <DynamicPieChart
              data={[consulted?.trueCount ?? 0, consulted?.falseCount ?? 0]}
              labels={["Consultados ficha limpa", "Consultados com restrições"]}
              dataTitle="Consultas"
              seed={7}
            />
          </div>
          {isMobile() ? (
            <div style={{ width: "300px", height: "300px", flex: 1 }}>
              <DynamicPieChart
                data={[
                  regionCount?.regions?.Norte ?? 0,
                  regionCount?.regions?.Nordeste ?? 0,
                  regionCount?.regions?.CentroOeste ?? 0,
                  regionCount?.regions?.Sudeste ?? 0,
                  regionCount?.regions?.Sul ?? 0,
                  regionCount?.regions?.Desconhecido ?? 0
                ]}
                dataTitle="Contas"
                labels={[
                  "Norte",
                  "Nordeste",
                  "Centro-Oeste",
                  "Sudeste",
                  "Sul",
                  "Desconhecido"
                ]}
                seed={8}
              />
            </div>
          ) : (
            <div style={{ width: "600px", flex: 1 }}>
              <DynamicBarChart
                data={[
                  regionCount?.regions?.Norte ?? 0,
                  regionCount?.regions?.Nordeste ?? 0,
                  regionCount?.regions?.CentroOeste ?? 0,
                  regionCount?.regions?.Sudeste ?? 0,
                  regionCount?.regions?.Sul ?? 0,
                  regionCount?.regions?.Desconhecido ?? 0
                ]}
                dataTitle="Contas"
                labels={[
                  "Norte",
                  "Nordeste",
                  "Centro-Oeste",
                  "Sudeste",
                  "Sul",
                  "Desconhecido"
                ]}
                seed={9}
              />
            </div>
          )}
        </SectionContainer>
        <SectionContainer>
          {isMobile() ? (
            <div style={{ width: "300px", height: "300px", flex: 1 }}>
              <DynamicPieChart
                data={[
                  scenarios?.prompts?.Criminal ?? 0,
                  scenarios?.prompts?.Trabalhista ?? 0,
                  scenarios?.prompts?.Cível ?? 0,
                  scenarios?.prompts?.Eleitoral ?? 0
                ]}
                dataTitle="Esfera de Análise"
                labels={["Criminal", "Trabalhista", "Cível", "Eleitoral"]}
                seed={10}
              />
            </div>
          ) : (
            <div style={{ width: "300px", flex: 1, marginRight: "2vw" }}>
              <DynamicBarChart
                data={[
                  scenarios?.prompts?.Criminal ?? 0,
                  scenarios?.prompts?.Trabalhista ?? 0,
                  scenarios?.prompts?.Cível ?? 0,
                  scenarios?.prompts?.Eleitoral ?? 0
                ]}
                dataTitle="Esfera de Análise"
                labels={["Criminal", "Trabalhista", "Cível", "Eleitoral"]}
                seed={10}
              />
            </div>
          )}
          {isMobile() ? (
            <div style={{ width: "300px", height: "300px", flex: 1 }}>
              <DynamicPieChart
                data={planCount?.plans?.map((plan) => plan.count) ?? []}
                dataTitle="Clientes por Plano"
                labels={planCount?.plans?.map((plan) => plan.name) ?? []}
                seed={11}
              />
            </div>
          ) : (
            <div style={{ width: "300px", flex: 1 }}>
              <DynamicBarChart
                data={planCount?.plans?.map((plan) => plan.count) ?? []}
                dataTitle="Clientes por Plano"
                labels={planCount?.plans?.map((plan) => plan.name) ?? []}
                seed={11}
              />
            </div>
          )}
        </SectionContainer>
        <SectionContainer style={{ justifyContent: "space-between" }}>
          <div style={{ width: isMobile() ? "100%" : "800px" }}>
            <DynamicLineChart
              labels={parseDailyLabels(dailySignups?.dailySignups ?? [])}
              data={parseDailyData(dailySignups?.dailySignups ?? [])}
              dataTitle="Qtd de cadastro por Data"
              seed={42}
            />
          </div>
          <div style={{ width: isMobile() ? "100%" : "800px" }}>
            <DynamicLineChart
              labels={parseDailyLabels(dailyProcesses?.dailyProcesses ?? [])}
              data={parseDailyData(dailyProcesses?.dailyProcesses ?? [])}
              dataTitle="Qtd de consultas por Data"
              seed={19}
            />
          </div>
        </SectionContainer>
      </MainContainer>
    </Page.Root>
  );
};

export default MasterDashboard;
